import React, { useState, useEffect, useRef, useCallback } from "react";
import Icon from "@mdi/react";
import { mdiCloseCircleOutline } from "@mdi/js";
import { fetchGet } from "../../../../service/api_client";
import { debounce } from "lodash";

interface Props {
  value: string;
  onChange: (val: string) => void;
  onClear: () => void;
  placeholder?: string;
  id?: string;
}

const CustomerGroupSearchField: React.FC<Props> = ({
  value,
  onChange,
  onClear,
  placeholder = "Customer Group",
  id,
}) => {
  const [searchTerm, setSearchTerm] = useState(value || "");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchTerm(value || "");
  }, [value]);

  const fetchSuggestions = async (query: string) => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/customer-groups?search=${encodeURIComponent(
        query.trim(),
      )}`;
      const res = await fetchGet(url);
      if (res.success) {
        setSuggestions(res.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(
    debounce((q: string) => fetchSuggestions(q), 400),
    [],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    setShowDropdown(true);
    debouncedFetch(val);
  };

  const handleSelect = (group: string) => {
    setSearchTerm(group);
    setShowDropdown(false);
    onChange(group);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="flex items-center gap-2 relative w-full"
      ref={wrapperRef}
      id={id}
    >
      <div className="relative flex-1 min-w-0">
        <input
          id={id ? `${id}-input` : undefined}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => {
            setShowDropdown(true);
            if (!suggestions.length) fetchSuggestions(searchTerm);
          }}
          className="h-10 w-full rounded-md border px-3 text-xs md:text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder={placeholder}
        />

        {showDropdown && (
          <div className="absolute top-full left-0 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-50">
            {loading ? (
              <div className="px-3 py-2 text-xs md:text-sm text-gray-500">
                Loading...
              </div>
            ) : suggestions.length > 0 ? (
              suggestions.map((group) => (
                <div
                  key={group}
                  id={id ? `${id}-option-${group}` : undefined}
                  className="px-3 py-2 text-xs md:text-sm hover:bg-gray-100 cursor-pointer text-left"
                  onClick={() => handleSelect(group)}
                >
                  <span className="font-medium text-gray-800">{group}</span>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-xs md:text-sm text-gray-500">
                No results found
              </div>
            )}
          </div>
        )}
      </div>

      {value && (
        <button
          id={id ? `${id}-clear` : undefined}
          onClick={() => {
            setSearchTerm("");
            onClear();
          }}
          className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
        >
          <Icon path={mdiCloseCircleOutline} size={0.7} />
        </button>
      )}
    </div>
  );
};

export default CustomerGroupSearchField;
